import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';

import { Box, FieldSet, Label, Legend, Radio } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';

import { useSelector } from '../../../../selectors/useSelector';

import WizardServerError from '../../../../model/WizardServerError';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';

import PropertiesActions from '../../../../actions/PropertiesActions';

import PostingActions from '../../../../actions/PostingActions';

import actions from '../../../../containers/CreateListingPage/actions';

import wizardActions from '../../../../components/Wizard/actions';

import { useForm } from '../../../../hooks';

import { postingConstraints } from '../../../../utils/formValidation';
import TimeConstants from '../../../../constants/TimeConstants';

import { WizardLabeledControl } from '../../../../styles/wizard';

import type { ChangeEvent } from 'react';

type Props = {
  isRelistingFieldset: boolean;
};

const radioControlName = 'hideAddress';

const HideAddressFieldSet = ({ isRelistingFieldset = false }: Props) => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { serverErrorObj, isInModalState },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { listingAlias, hideAddress } = useSelector((state) => state.properties.currentProperty);

  const {
    inputs,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm({ hideAddress: hideAddress.toString() }, postingConstraints);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, name: fieldName } = event.target;

    if (hasFieldError(fieldName, value)) {
      return;
    }

    const dataToSave = {
      fieldName,
      value,
      propertyId: listingAlias,
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PostingActions.property.edit(dataToSave))
      // @ts-expect-error
      .then(() => {
        dispatch(PropertiesActions.updateCurrentProperty({ [fieldName]: value === 'true' }));
      })
      .catch((message: string) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };
  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist();
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
    saveDebounced(event);
  };

  return (
    <FieldSet
      legend={<Legend size={isRelistingFieldset ? 'lg' : 'md'}>Hide property address</Legend>}
      className="HideAddressFieldSet"
      required={false}
    >
      <Box
        css={{
          display: 'flex',
          paddingLeft: { base: '200', lg: isRelistingFieldset ? '0' : '200' },
          flexDirection: { base: 'column', lg: isRelistingFieldset ? 'row' : 'column' },
        }}
      >
        <WizardLabeledControl
          controlId="false"
          label={
            <Label>
              No&nbsp;
              <strong>(Recommended)</strong>
            </Label>
          }
          control={
            <Radio
              name={radioControlName}
              value="false"
              checked={inputs.hideAddress === 'false'}
              onChange={handleChange}
            />
          }
        />

        <WizardLabeledControl
          controlId="true"
          label={<Label>Yes</Label>}
          control={
            <Radio
              name={radioControlName}
              value="true"
              checked={inputs.hideAddress === 'true'}
              onChange={handleChange}
            />
          }
        />
      </Box>
    </FieldSet>
  );
};

export default HideAddressFieldSet;
